import AssignmentFeedbackPanel from 'components/CreateAssignment/AssignmentFeedbackPanel';
import AssignmentGradingPanel from 'components/CreateAssignment/AssignmentGradingPanel';
import AssignmentInlineCommentsPanel from 'components/CreateAssignment/AssignmentInlineCommentsPanel';
import GeneralSection from 'components/CreateAssignment/GeneralSection';
import { useApp } from 'contexts/app';
import { useViewport } from 'contexts/viewport';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from 'shell/components/Box';
import Button from 'shell/components/Button';
import Container from 'shell/components/Container';
import Icon from 'shell/components/Icon';
import Stack from 'shell/components/Stack';
import Tabs from 'shell/components/Tabs';
import Text from 'shell/components/Text';
import TextInput from 'shell/components/TextInput';
import { getAssignmentSettingsErrors } from 'utils/assignmentSettingsValidation';

const errorTab = (errors) => {
    if (errors.title || errors.description || errors.rubric || errors.submissionTypes) return 'description';
    if (errors.gradingSettings) return 'grading';
    if (errors.feedbackSettings) return 'feedback';
    if (errors.studentFeedbackSettings) return 'formative';
    if (errors.inlineCommentsSettings) return 'inline-comments';
    return null;
};

const AssignmentSettingsEditor = ({
    batch,
    submissions,
    form,
    setForm,
    errors = {},
    setErrors,
    onClickBack,
    onClickSave,
    isNew = false,
    isDraft = false,
    loading = false,
    isSaving = false,
    settingsRevision = 0,
    ...rest
}) => {
    const { device } = useViewport();
    const restrictions = useApp();
    const { t } = useTranslation('assignmentSettings');
    const [tab, setTab] = useState('description');
    const [hasPendingDraft, setHasPendingDraft] = useState(false);
    const [saveError, setSaveError] = useState('');
    const isEditable = !submissions?.length && !batch?.length;
    const busy = loading || isSaving;
    const panelProps = { ...rest, form, setForm, errors, setErrors, isEditable };
    const handleSave = (key) => {
        if (hasPendingDraft) {
            setTab('grading');
            setSaveError('pending-draft');
            return;
        }
        const invalid = getAssignmentSettingsErrors(form, restrictions);
        const target = errorTab(invalid);
        if (target) {
            setErrors((current) => ({ ...current, ...invalid }));
            setTab(target);
            setSaveError('invalid-settings');
            return;
        }
        // Publish validation remains in the provider; bring missing required fields into view.
        if (key === 'publish' && (!form.title?.trim() || !form.description?.trim() || !form.rubric))
            setTab('description');
        setSaveError('');
        onClickSave({ key });
    };
    const tabs = [
        {
            key: 'description',
            value: t('tabs.description', 'Description & Settings'),
            content: <GeneralSection {...panelProps} />,
        },
        {
            key: 'grading',
            value: t('tabs.grading', 'Grading Settings'),
            content: (
                <AssignmentGradingPanel
                    {...panelProps}
                    draftResetKey={settingsRevision}
                    onPendingDraftChange={setHasPendingDraft}
                />
            ),
        },
        {
            key: 'feedback',
            value: t('tabs.feedback', 'Feedback Settings'),
            content: <AssignmentFeedbackPanel {...panelProps} />,
        },
        {
            key: 'formative',
            value: t('tabs.formative', 'Formative Feedback'),
            content: <AssignmentFeedbackPanel {...panelProps} student />,
        },
        {
            key: 'inline-comments',
            value: t('tabs.inline-comments', 'Inline Comments'),
            content: <AssignmentInlineCommentsPanel {...panelProps} />,
        },
    ];

    return (
        <Container
            device={device}
            sx={(theme) => ({
                background: theme.palette.ensightful.shades[0],
                alignItems: 'flex-start',
                px: device === 'mobile' ? 4 : 8,
                pb: 8,
                maxWidth: 'none !important',
            })}
        >
            <Stack spacing={4} sx={{ width: '100%', minWidth: 0 }}>
                <Stack
                    direction={'row'}
                    alignItems={'center'}
                    justifyContent={'space-between'}
                    useFlexGap
                    flexWrap={'wrap'}
                    spacing={2}
                >
                    <Button
                        variant={'borderless'}
                        startIcon={<Icon kind={'arrowLeft'} />}
                        onClick={onClickBack}
                        disabled={busy}
                    >
                        {t('common:controls.back', 'Back')}
                    </Button>
                    <Stack direction={'row'} useFlexGap flexWrap={'wrap'} spacing={2}>
                        <Button variant={'borderless'} color={'neutral'} onClick={onClickBack} disabled={busy}>
                            {t('common:controls.cancel', 'Cancel')}
                        </Button>
                        <Button variant={'borderless'} disabled={busy} onClick={() => handleSave('save')}>
                            {isNew || isDraft
                                ? t('common:controls.save-as-draft', 'Save as Draft')
                                : t('common:controls.unpublish', 'Unpublish')}
                        </Button>
                        <Button
                            variant={'borderless'}
                            loading={busy}
                            disabled={busy}
                            onClick={() => handleSave('publish')}
                        >
                            {isNew || isDraft
                                ? t('common:controls.publish', 'Publish')
                                : t('common:controls.save', 'Save')}
                        </Button>
                    </Stack>
                </Stack>
                <TextInput
                    inputProps={{ 'aria-label': t('title-label', 'Assignment title') }}
                    placeholder={t('title-placeholder', 'Enter assignment title')}
                    value={form.title}
                    error={Boolean(errors.title)}
                    disabled={busy}
                    onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
                    sx={(theme) => ({
                        maxWidth: 780,
                        '&& > div > input': {
                            fontSize: '1.25rem',
                            fontWeight: theme.typography.sh1.fontWeight.semibold,
                            px: 0,
                        },
                        '&& > div > fieldset': { borderColor: errors.title ? undefined : 'transparent' },
                    })}
                />
                {saveError && (saveError !== 'pending-draft' || hasPendingDraft) && (
                    <Text role={'alert'} color={'error'}>
                        {saveError === 'pending-draft'
                            ? t('pending-draft', 'Add or cancel your instruction draft before saving.')
                            : t('invalid-settings', 'Review the highlighted settings before saving.')}
                    </Text>
                )}
                {!isEditable && (
                    <Box
                        sx={(theme) => ({
                            maxWidth: 780,
                            p: 3,
                            borderRadius: '8px',
                            background: theme.palette.ensightful.warning[100],
                        })}
                    >
                        <Text>
                            {t(
                                'assignment:create-assignment.edit-restriction-message',
                                'Editing assignment details after upload is restricted to keep grades and feedback consistent. Duplicate the assignment to change these settings.'
                            )}
                        </Text>
                    </Box>
                )}
                <Tabs
                    aria-label={t('tabs-label', 'Assignment settings')}
                    appearance={'underline'}
                    keepMounted
                    options={tabs}
                    selected={tab}
                    onChange={setTab}
                    sx={{
                        width: '100%',
                        minWidth: 0,
                        '& > [role="tabpanel"]': { maxWidth: 780, pt: 5 },
                        '& .MuiTabs-root': { maxWidth: 820 },
                    }}
                />
            </Stack>
        </Container>
    );
};

export default AssignmentSettingsEditor;
